import { useState, useEffect } from "react";
import { Modal, Table, Alert, Button, Form, ListGroup } from "react-bootstrap";
import { getBitrixTasks, attachCheckToBitrixTask } from "./apiBitrix";
import {
    getProductMappingsBySourceIds,
    getProductSizeRangeMappingsBySourceIds,
    getAdditionMappingsBySourceIds,
    getAdditionSizeRangeMappingsBySourceIds,
    getNewProducts,
} from "../Page/apiForNewProducts";

export default function ExportToSendsModal({
    show,
    onHide,
    updates,
    amounts,
    optProducts,
    jwt,
    onDownloadForBitrix,
    buildCheckForBitrix
}) {
    const [bitrixTasks, setBitrixTasks] = useState([]);
    const [taskSearch, setTaskSearch] = useState("");
    const [tasksLoading, setTasksLoading] = useState(false);
    const [tasksError, setTasksError] = useState(false);
    const [selectedTaskId, setTaskSelected] = useState(null);
    const [attaching, setAttaching] = useState(false);
    const [attachResult, setAttachResult] = useState(null);

    useEffect(() => {
        if (!show) return;
        setTasksLoading(true);
        setTasksError(false);
        // debounce, чтобы не дёргать Bitrix на каждую букву
        const timer = setTimeout(async () => {
            const list = await getBitrixTasks(taskSearch.trim(), jwt);
            if (list === null) {
                setTasksError(true);
                setBitrixTasks([]);
            } else {
                setBitrixTasks(list);
            }
            setTasksLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [show, taskSearch, jwt]);

    useEffect(() => {
        if (show) {
            setAttachResult(null);
            setTaskSelected(null);
        }
    }, [show]);

    const handleAttach = async () => {
        if (!selectedTaskId || !buildCheckForBitrix) return;
        setAttaching(true);
        setAttachResult(null);
        const ok = await attachCheckToBitrixTask(selectedTaskId, buildCheckForBitrix(5, ''), jwt);
        setAttaching(false);
        if (ok) {
            setBitrixTasks(prev => prev.filter(t => t.id !== selectedTaskId));
            setTaskSelected(null);
            setAttachResult({ ok: true, text: "Файл расчёта прикреплён к задаче" });
        } else {
            setAttachResult({ ok: false, text: "Не удалось прикрепить файл к задаче" });
        }
    };

    const [mappings, setMappings] = useState([]);
    const [additionMappings, setAdditionMappings] = useState([]);
    const [unmappedAdditionIds, setUnmappedAdditionIds] = useState([]);
    const [sendProducts, setSendProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) return;

        async function loadData() {
            setLoading(true);
            setError(null);

            try {
                const sourceTypeMap = {};

                const standardIds = [];
                updates.filter(p => !p.isRemoved).forEach(p => {
                    const id = Number(p.id);
                    standardIds.push(id);
                    sourceTypeMap[id] = "Standard";
                });

                const optIds = [];
                optProducts.filter(p => p && !p.isRemoved && p.name && p.id).forEach(p => {
                    const id = Number(p.id);
                    optIds.push(id);
                    sourceTypeMap[id] = "Opt";
                });

                const allSourceIds = [...standardIds, ...optIds];

                const allProducts = [
                    ...updates.filter(p => !p.isRemoved).map(p => ({ ...p, _srcType: "Standard" })),
                    ...optProducts.filter(p => p && !p.isRemoved && p.name && p.id).map(p => ({ ...p, _srcType: "Opt" })),
                ];

                // Собираем использующиеся допики построчно, с размером и типом родительского товара —
                // тем же способом, что и BitrixService.CreateOrderFromTaskAsync на бэкенде
                const additionLines = [];
                allProducts.forEach(p => {
                    (p.additions || []).forEach(a => {
                        if (!a.isUse) return;
                        const perUnitQty = Number(a.quantity) || 1;
                        const productQty = Number(p.quantity ?? p.amount) || 0;
                        additionLines.push({
                            additionId: Number(a.id),
                            sourceType: p._srcType,
                            parentSizeUser: p.sizeUser != null ? Number(p.sizeUser) : null,
                            quantity: perUnitQty * productQty,
                        });
                    });
                });

                const standardAdditionIds = [...new Set(additionLines.filter(l => l.sourceType === "Standard").map(l => l.additionId))];
                const optAdditionIds = [...new Set(additionLines.filter(l => l.sourceType === "Opt").map(l => l.additionId))];

                if (allSourceIds.length === 0 && additionLines.length === 0) {
                    setMappings([]);
                    setAdditionMappings([]);
                    setUnmappedAdditionIds([]);
                    setSendProducts([]);
                    setLoading(false);
                    return;
                }

                // Грузим обычные маппинги и диапазонные параллельно (товары + допики)
                const [
                    stdMappings,
                    optMappings,
                    stdRanges,
                    optRanges,
                    stdAdditionMappings,
                    optAdditionMappings,
                    stdAdditionRanges,
                    optAdditionRanges,
                ] = await Promise.all([
                    standardIds.length > 0 ? getProductMappingsBySourceIds("Standard", standardIds, jwt) : Promise.resolve([]),
                    optIds.length > 0 ? getProductMappingsBySourceIds("Opt", optIds, jwt) : Promise.resolve([]),
                    standardIds.length > 0 ? getProductSizeRangeMappingsBySourceIds("Standard", standardIds, jwt) : Promise.resolve([]),
                    optIds.length > 0 ? getProductSizeRangeMappingsBySourceIds("Opt", optIds, jwt) : Promise.resolve([]),
                    standardAdditionIds.length > 0 ? getAdditionMappingsBySourceIds("Standard", standardAdditionIds, jwt) : Promise.resolve([]),
                    optAdditionIds.length > 0 ? getAdditionMappingsBySourceIds("Opt", optAdditionIds, jwt) : Promise.resolve([]),
                    standardAdditionIds.length > 0 ? getAdditionSizeRangeMappingsBySourceIds("Standard", standardAdditionIds, jwt) : Promise.resolve([]),
                    optAdditionIds.length > 0 ? getAdditionSizeRangeMappingsBySourceIds("Opt", optAdditionIds, jwt) : Promise.resolve([]),
                ]);

                const allRegularMappings = [...(stdMappings || []), ...(optMappings || [])];
                const allRanges = [...(stdRanges || []), ...(optRanges || [])];

                // Группируем диапазоны: `${sourceProductId}_${sourceType}` -> ranges[]
                const rangesMap = {};
                allRanges.forEach(r => {
                    const key = `${r.sourceProductId}_${r.sourceType}`;
                    if (!rangesMap[key]) rangesMap[key] = [];
                    rangesMap[key].push(r);
                });

                // Строим финальные маппинги: для каждого продукта
                // если есть диапазоны — подбираем по sizeUser, иначе fallback на простой маппинг
                const effectiveMappings = [];

                allProducts.forEach(p => {
                    const id = Number(p.id);
                    const srcType = p._srcType;
                    const key = `${id}_${srcType}`;
                    const productRanges = rangesMap[key];

                    const findRegular = () => allRegularMappings.find(m =>
                        Number(m.sourceProductId || m.SourceProductId) === id &&
                        (m.sourceType || m.SourceType) === srcType
                    );

                    let matched = null;
                    if (productRanges && productRanges.length > 0) {
                        const sizeUser = p.sizeUser != null ? Number(p.sizeUser) : null;
                        const rangeMatch = productRanges.find(r => {
                            const fromOk = r.sizeFrom == null || (sizeUser != null && sizeUser >= r.sizeFrom);
                            const toOk = r.sizeTo == null || (sizeUser != null && sizeUser <= r.sizeTo);
                            return fromOk && toOk;
                        });
                        if (rangeMatch) {
                            matched = {
                                newProductId: rangeMatch.newProductId,
                                newProductName: rangeMatch.newProductName,
                            };
                        } else {
                            // Диапазоны заданы, но размер ни в один не попал — откатываемся
                            // на простую привязку, если она есть (согласовано с поведением
                            // BitrixService.CreateOrderFromTaskAsync)
                            const regular = findRegular();
                            if (regular) {
                                matched = {
                                    newProductId: regular.newProductId || regular.NewProductId,
                                    newProductName: regular.newProductName || regular.NewProductName,
                                };
                            }
                        }
                    } else {
                        // Нет диапазонов — ищем обычный маппинг
                        const regular = findRegular();
                        if (regular) {
                            matched = {
                                newProductId: regular.newProductId || regular.NewProductId,
                                newProductName: regular.newProductName || regular.NewProductName,
                            };
                        }
                    }

                    if (matched) {
                        effectiveMappings.push({
                            sourceProductId: id,
                            sourceType: srcType,
                            newProductId: matched.newProductId,
                            newProductName: matched.newProductName,
                        });
                    }
                });

                setMappings(effectiveMappings);

                // То же самое резолвим для допиков — диапазон по размеру родительского товара ->
                // простая привязка (AdditionMappings) -> непривязан
                const allAdditionRegularMappings = [...(stdAdditionMappings || []), ...(optAdditionMappings || [])];
                const allAdditionRanges = [...(stdAdditionRanges || []), ...(optAdditionRanges || [])];

                const additionRangesMap = {};
                allAdditionRanges.forEach(r => {
                    const key = `${r.sourceAdditionId}_${r.sourceType}`;
                    if (!additionRangesMap[key]) additionRangesMap[key] = [];
                    additionRangesMap[key].push(r);
                });

                const effectiveAdditionMappingsByTarget = {};
                const unmappedAdditions = [];

                additionLines.forEach(line => {
                    const key = `${line.additionId}_${line.sourceType}`;
                    const additionRanges = additionRangesMap[key];

                    const findRegular = () => allAdditionRegularMappings.find(m =>
                        Number(m.sourceAdditionId || m.SourceAdditionId) === line.additionId &&
                        (m.sourceType || m.SourceType) === line.sourceType
                    );
                    const toMatch = (m) => ({
                        newProductId: m.newProductId || m.NewProductId,
                        newProductName: m.newProductName || m.NewProductName,
                    });

                    let matched = null;
                    if (additionRanges && additionRanges.length > 0) {
                        const rangeMatch = additionRanges.find(r => {
                            const fromOk = r.sizeFrom == null || (line.parentSizeUser != null && line.parentSizeUser >= r.sizeFrom);
                            const toOk = r.sizeTo == null || (line.parentSizeUser != null && line.parentSizeUser <= r.sizeTo);
                            return fromOk && toOk;
                        });
                        if (rangeMatch) {
                            matched = toMatch(rangeMatch);
                        } else {
                            const regular = findRegular();
                            matched = regular ? toMatch(regular) : null;
                        }
                    } else {
                        const regular = findRegular();
                        matched = regular ? toMatch(regular) : null;
                    }

                    if (matched) {
                        const groupKey = `${line.additionId}_${line.sourceType}_${matched.newProductId}`;
                        if (!effectiveAdditionMappingsByTarget[groupKey]) {
                            effectiveAdditionMappingsByTarget[groupKey] = {
                                sourceAdditionId: line.additionId,
                                sourceType: line.sourceType,
                                newProductId: matched.newProductId,
                                newProductName: matched.newProductName,
                                quantity: 0,
                            };
                        }
                        effectiveAdditionMappingsByTarget[groupKey].quantity += line.quantity;
                    } else {
                        unmappedAdditions.push(line.additionId);
                    }
                });

                setAdditionMappings(Object.values(effectiveAdditionMappingsByTarget));
                setUnmappedAdditionIds([...new Set(unmappedAdditions)]);

                const newProductIds = [
                    ...effectiveMappings.map(m => m.newProductId),
                    ...Object.values(effectiveAdditionMappingsByTarget).map(m => m.newProductId),
                ];
                if (newProductIds.length > 0) {
                    const productsResponse = await getNewProducts(jwt);
                    setSendProducts(productsResponse || []);
                } else {
                    setSendProducts([]);
                }
            } catch (err) {
                console.error("Error loading mappings:", err);
                setError("Ошибка загрузки данных");
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [show, updates, optProducts, jwt]);

    const sourceProductIds = new Set();
    updates.filter(p => !p.isRemoved).forEach(p => sourceProductIds.add(Number(p.id)));
    optProducts.filter(p => p && !p.isRemoved && p.name && p.id).forEach(p => sourceProductIds.add(Number(p.id)));

    const mappedProductIds = new Set(mappings.map(m => Number(m.sourceProductId)));
    const unmappedIds = [...sourceProductIds].filter(id => !mappedProductIds.has(id));

    const getProductName = (productId, srcType) => {
        if (srcType === "Standard" || srcType === "standard") {
            const product = updates.find(p => Number(p.id) === productId);
            return product?.name || `Продукт ${productId}`;
        } else {
            const product = optProducts.find(p => Number(p.id) === productId);
            return product?.name || `Опт продукт ${productId}`;
        }
    };

    const getAdditionName = (additionId, srcType) => {
        const allProducts = [...updates, ...optProducts].filter(Boolean);
        for (const p of allProducts) {
            const found = (p.additions || []).find(a => Number(a.id) === additionId);
            if (found?.name) return found.name;
        }
        return `Допик ${additionId}`;
    };

    const getSendProductName = (sendProductId) => {
        const product = sendProducts.find(p => Number(p.id) === sendProductId);
        return product?.name || product?.Name || `Отправка ${sendProductId}`;
    };

    const getSourceProductQuantity = (sourceProductId) => {
        const product = updates.find(p => Number(p.id) === Number(sourceProductId));
        if (!product) return "-";
        return product?.quantity ?? product?.amount ?? "-";
    };

    const groupedBySendProduct = {};
    mappings.forEach(m => {
        const key = m.newProductId;
        if (!groupedBySendProduct[key]) {
            groupedBySendProduct[key] = {
                sendProductId: key,
                sendProductName: getSendProductName(key),
                sourceProducts: []
            };
        }
        groupedBySendProduct[key].sourceProducts.push({
            id: m.sourceProductId,
            name: getProductName(m.sourceProductId, m.sourceType),
            quantity: getSourceProductQuantity(m.sourceProductId)
        });
    });

    const groupedList = Object.values(groupedBySendProduct);

    const groupedAdditionsBySendProduct = {};
    additionMappings.forEach(m => {
        const key = m.newProductId;
        if (!groupedAdditionsBySendProduct[key]) {
            groupedAdditionsBySendProduct[key] = {
                sendProductId: key,
                sendProductName: getSendProductName(key),
                additions: []
            };
        }
        groupedAdditionsBySendProduct[key].additions.push({
            id: m.sourceAdditionId,
            name: getAdditionName(m.sourceAdditionId, m.sourceType),
            quantity: m.quantity
        });
    });

    const groupedAdditionsList = Object.values(groupedAdditionsBySendProduct);

    const hasUnmapped = unmappedIds.length > 0 || unmappedAdditionIds.length > 0;

    const handleDownloadForBitrix = () => {
        if (!onDownloadForBitrix) return;
        onDownloadForBitrix(5, '');
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Экспорт в отправки</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center p-3">Загрузка...</div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : hasUnmapped ? (
                    <Alert variant="warning" className="mb-3">
                        <strong>Внимание:</strong> следующие продукты и допики не связаны с продукцией отправок:
                        <ul className="mb-0 mt-2">
                            {unmappedIds.map(id => (
                                <li key={`p-${id}`}>
                                    {getProductName(id, updates.find(p => Number(p.id) === id) ? "Standard" : "Opt")}
                                </li>
                            ))}
                            {unmappedAdditionIds.map(id => (
                                <li key={`a-${id}`}>
                                    {getAdditionName(id)} (допик)
                                </li>
                            ))}
                        </ul>
                    </Alert>
                ) : (
                    <Alert variant="info" className="mb-3">
                        Все продукты и допики связаны с продукцией отправок
                    </Alert>
                )}

                {groupedList.length > 0 && (
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th style={{ width: "10%" }}>Кол-во</th>
                                <th style={{ width: "35%" }}>Продукция калькулятора</th>
                                <th style={{ width: "35%" }}>Продукция отправок</th>
                                <th style={{ width: "20%" }}>Всего</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedList.map(group => {
                                const totalQty = group.sourceProducts.reduce((sum, sp) => sum + (Number(sp.quantity) || 0), 0);
                                return (
                                    <tr key={group.sendProductId}>
                                        <td>
                                            {group.sourceProducts.map(sp => (
                                                <div key={sp.id}>{sp.quantity || "-"}</div>
                                            ))}
                                        </td>
                                        <td>
                                            {group.sourceProducts.map(sp => (
                                                <div key={sp.id}>{sp.name}</div>
                                            ))}
                                        </td>
                                        <td>
                                            <strong>{group.sendProductName}</strong>
                                            <div className="text-muted small">ID: {group.sendProductId}</div>
                                        </td>
                                        <td>
                                            <strong>{totalQty}</strong>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}

                {groupedAdditionsList.length > 0 && (
                    <>
                        <div className="mt-3 mb-2"><strong>Допики</strong></div>
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: "10%" }}>Кол-во</th>
                                    <th style={{ width: "35%" }}>Допик</th>
                                    <th style={{ width: "35%" }}>Продукция отправок</th>
                                    <th style={{ width: "20%" }}>Всего</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedAdditionsList.map(group => {
                                    const totalQty = group.additions.reduce((sum, a) => sum + (Number(a.quantity) || 0), 0);
                                    return (
                                        <tr key={`add-${group.sendProductId}`}>
                                            <td>
                                                {group.additions.map(a => (
                                                    <div key={a.id}>{a.quantity || "-"}</div>
                                                ))}
                                            </td>
                                            <td>
                                                {group.additions.map(a => (
                                                    <div key={a.id}>{a.name}</div>
                                                ))}
                                            </td>
                                            <td>
                                                <strong>{group.sendProductName}</strong>
                                                <div className="text-muted small">ID: {group.sendProductId}</div>
                                            </td>
                                            <td>
                                                <strong>{totalQty}</strong>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </>
                )}

                {sourceProductIds.size === 0 && (
                    <div className="text-center text-muted p-3">
                        Нет продуктов для экспорта
                    </div>
                )}

                {buildCheckForBitrix && sourceProductIds.size > 0 && (
                    <div className="mt-4">
                        <div className="mb-2"><strong>Прикрепить к задаче Bitrix</strong></div>
                        <Form.Control
                            size="sm"
                            className="mb-2"
                            placeholder="Поиск по названию задачи"
                            value={taskSearch}
                            onChange={e => setTaskSearch(e.target.value)}
                        />
                        {tasksError ? (
                            <Alert variant="danger" className="py-2">Не удалось загрузить задачи из Bitrix</Alert>
                        ) : tasksLoading ? (
                            <div className="text-muted small">Загрузка задач...</div>
                        ) : bitrixTasks.length === 0 ? (
                            <div className="text-muted small">Нет незавершённых задач без файла</div>
                        ) : (
                            <ListGroup style={{ maxHeight: 220, overflowY: "auto" }}>
                                {bitrixTasks.map(t => (
                                    <ListGroup.Item
                                        key={t.id}
                                        action
                                        active={t.id === selectedTaskId}
                                        onClick={() => setTaskSelected(t.id)}
                                        className="py-2"
                                    >
                                        <span className="text-muted small me-2">#{t.id}</span>{t.title}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                        {attachResult && (
                            <Alert variant={attachResult.ok ? "success" : "danger"} className="py-2 mt-2 mb-0">
                                {attachResult.text}
                            </Alert>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Закрыть
                </Button>
                <Button
                    variant="primary"
                    onClick={handleAttach}
                    disabled={hasUnmapped || loading || !selectedTaskId || attaching}
                >
                    {attaching ? "Прикрепление..." : "Прикрепить к задаче"}
                </Button>
                <Button
                    variant="success"
                    onClick={handleDownloadForBitrix}
                    disabled={hasUnmapped || loading}
                >
                    Скачать для Bitrix
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
